import { useEffect, useState, useCallback } from 'react';
import { Dialog } from '../../ui/Dialog/Dialog';
import { IdentifyCaptureStep } from './IdentifyCaptureStep';
import {
  IdentifyResultsStep,
  OWN_NAME_OPTION_ID,
  taxonomySummary,
  type IdentifyCandidate,
  type OwnIdentification,
} from './IdentifyResultsStep';
import { IdentifyPrefillFormStep, type IdentifiedCandidate } from './IdentifyPrefillFormStep';
import type { LivestockFormSuccessOptions } from '../LivestockForm';
import { useLivestockIdentification } from '../../../hooks/useLivestockIdentification';
import { useTranslation } from '../../../i18n/useTranslation';
import { getAiLimitErrorKind, getAiLimitResetsAt } from '../../../services/aiUsageService';
import {
  submitIdentificationFeedback,
  type LivestockIdentificationFeedback,
  type ShownIdentificationCandidate,
} from '../../../services/livestockIdentificationService';
import { formatResetDate } from '../../aiUsage/AiLimitNotice';
import { ApiError } from '../../../lib/apiClient';
import { uploadGalleryImages } from '../../../services/galleryService';
import { linkLivestockImage } from '../../../services/livestockService';

/** The API refused the photo itself (too large, not a JPEG/PNG/WEBP image, missing). */
function isPhotoRejected(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 413 || err.status === 400);
}

/** The suggestions as shown, without explanation texts (what the feedback stores). */
function shownCandidates(candidates: IdentifyCandidate[]): ShownIdentificationCandidate[] {
  return candidates.slice(0, 10).map(({ category, taxonomy, commonNames, confidence }) => ({
    category,
    taxonomy,
    commonNames: commonNames.slice(0, 10),
    confidence,
  }));
}

const same = (a: string | null | undefined, b: string | null | undefined): boolean =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

type IdentifyStep = 'capture' | 'results' | 'prefill';

export interface LivestockIdentifyFlowProps {
  /** The aquarium the identified livestock will belong to. */
  aquariumId: string;
  /** Whether the flow's dialog is open. */
  isOpen: boolean;
  /** Called when the flow closes for any reason (cancel, no-match, or after a successful save). */
  onClose: () => void;
  /** Ref to the button that opened the flow; focus is restored to it on close. */
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  /** Called after a livestock item has been successfully saved via the prefill form. */
  /** Called after a save; `photoAttachFailed` when the record was saved but its photo could not be linked. */
  onSaved: (result?: { photoAttachFailed: boolean }) => void;
}

function mapToIdentifiedCandidate(candidate: IdentifyCandidate): IdentifiedCandidate {
  const category: IdentifiedCandidate['category'] =
    candidate.category === 'coral' ||
    candidate.category === 'fish' ||
    candidate.category === 'invertebrate'
      ? candidate.category
      : 'other';

  return {
    category,
    taxonomy: candidate.taxonomy,
    commonNames: candidate.commonNames,
    confidence: candidate.confidence,
    featureExplanation: candidate.featureExplanation,
  };
}

export function LivestockIdentifyFlow({
  aquariumId,
  isOpen,
  onClose,
  triggerRef,
  onSaved,
}: LivestockIdentifyFlowProps) {
  const { t, i18n } = useTranslation();
  const { submitPhotoAsync, deleteTempPhoto } = useLivestockIdentification();

  const [step, setStep] = useState<IdentifyStep>('capture');
  const [candidates, setCandidates] = useState<IdentifyCandidate[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tempPhotoKey, setTempPhotoKey] = useState<string | undefined>(undefined);
  // The photo the user identified — kept to add it to the gallery and the saved animal.
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ownIdentification, setOwnIdentification] = useState<OwnIdentification | null>(null);

  const resetState = useCallback(() => {
    setStep('capture');
    setCandidates([]);
    setSelectedId(null);
    setTempPhotoKey(undefined);
    setPhotoFile(null);
    setIsSubmitting(false);
    setError(null);
    setOwnIdentification(null);
  }, []);

  // Learning from corrections: the outcome is stored for the signed-in user and their own
  // confirmed names are sent with their later identifications. Never blocks the flow.
  const sendFeedback = useCallback(
    (feedback: Omit<LivestockIdentificationFeedback, 'language' | 'photoKey'>) => {
      Promise.resolve()
        .then(() =>
          submitIdentificationFeedback(aquariumId, {
            ...feedback,
            photoKey: tempPhotoKey ?? null,
            language: i18n.language ? i18n.language.slice(0, 10) : null,
          }),
        )
        .catch(() => {
        // Feedback is best effort — the livestock record itself is already saved.
      });
    },
    [aquariumId, i18n.language, tempPhotoKey],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    // Reset whenever the flow is (re)opened so a stale previous run never leaks through.
    resetState();
  }, [isOpen, resetState]);

  // Focus goes back to the trigger after the dialog's own focus handling has run.
  const restoreFocus = useCallback(() => {
    requestAnimationFrame(() => {
      triggerRef.current?.focus();
    });
  }, [triggerRef]);

  const closeAndRestoreFocus = useCallback(() => {
    resetState();
    onClose();
    restoreFocus();
  }, [onClose, resetState, restoreFocus]);

  const handleCancel = useCallback(() => {
    if (tempPhotoKey) {
      deleteTempPhoto({ aquariumId, key: tempPhotoKey });
    }
    closeAndRestoreFocus();
  }, [aquariumId, closeAndRestoreFocus, deleteTempPhoto, tempPhotoKey]);

  const handleCaptureSubmit = useCallback(
    (file: File) => {
      setIsSubmitting(true);
      setError(null);
      // The answer (common names, explanations) comes back in the UI language.
      setPhotoFile(file);
      submitPhotoAsync({ aquariumId, file, language: i18n.language })
        .then((result) => {
          const candidatesWithIds: IdentifyCandidate[] = result.candidates.map(
            // Keep an id the API already supplied; otherwise derive a stable one.
            (candidate, index) => ({
              id: `candidate-${index}`,
              ...candidate,
            }),
          );
          setCandidates(candidatesWithIds);
          setTempPhotoKey(result.tempPhotoKey);
          setSelectedId(candidatesWithIds.length > 0 ? candidatesWithIds[0].id : null);
          setStep('results');
          setIsSubmitting(false);
        })
        .catch((err: unknown) => {
          // Monthly AI credits used up / app-wide AI budget reached: say so.
          const aiLimit = getAiLimitErrorKind(err);
          if (aiLimit === 'unavailable') {
            setError(t('aiUsage.unavailable'));
          } else if (aiLimit === 'exceeded') {
            const date = formatResetDate(getAiLimitResetsAt(err), i18n.language);
            setError(
              `${date !== null ? t('aiUsage.exceeded', { date }) : t('aiUsage.exceededNoDate')} ${t('aiUsage.supportHint')}`,
            );
          } else if (isPhotoRejected(err)) {
            setError(t('identify.capture.error.body'));
          } else {
            setError(t('identify.capture.genericError'));
          }
          setIsSubmitting(false);
        });
    },
    [aquariumId, submitPhotoAsync, t, i18n.language],
  );

  const handleRetryCapture = useCallback(() => {
    setError(null);
  }, []);

  const handleSelectCandidate = useCallback((id: string) => {
    setSelectedId(id);
  }, []);

  const handleAccept = useCallback(
    (id: string) => {
      setSelectedId(id);
      setOwnIdentification(null);
      setStep('prefill');
    },
    [],
  );

  const handleAcceptOwn = useCallback((own: OwnIdentification) => {
    setSelectedId(OWN_NAME_OPTION_ID);
    setOwnIdentification(own);
    setStep('prefill');
  }, []);

  const handleNoMatch = useCallback(() => {
    sendFeedback({ outcome: 'no_match', candidates: shownCandidates(candidates) });
    handleCancel();
  }, [candidates, handleCancel, sendFeedback]);

  const handleResultsRetry = useCallback(() => {
    setCandidates([]);
    setSelectedId(null);
    setError(null);
    setStep('capture');
  }, []);

  const selectedCandidate = candidates.find((candidate) => candidate.id === selectedId) ?? null;
  const prefillCandidate = selectedCandidate ? mapToIdentifiedCandidate(selectedCandidate) : null;

  /**
   * Adds the identification photo to the aquarium gallery and links it to the new animal
   * (its list thumbnail). Resolves false when that failed — the animal itself is saved.
   */
  const attachPhoto = useCallback(
    async (livestockId: string, file: File): Promise<boolean> => {
      try {
        const [image] = await uploadGalleryImages(aquariumId, [file]);
        if (!image) return false;
        // The photo the animal was identified from is its main photo (list thumbnail).
        await linkLivestockImage(aquariumId, livestockId, image.id, { main: true });
        return true;
      } catch {
        return false;
      }
    },
    [aquariumId],
  );

  const handlePrefillSaved = useCallback(
    async (created: { id: string } | undefined, options?: LivestockFormSuccessOptions) => {
      const saved = options?.saved;
      if (saved) {
        const index = selectedCandidate ? candidates.indexOf(selectedCandidate) : -1;
        // Saved exactly as suggested → accepted; the user's own or an edited name → corrected.
        const unchanged =
          !ownIdentification &&
          selectedCandidate !== null &&
          same(saved.commonName, selectedCandidate.commonNames[0]) &&
          same(saved.scientificName, taxonomySummary(selectedCandidate.taxonomy));
        sendFeedback({
          outcome: unchanged ? 'accepted' : 'corrected',
          candidates: shownCandidates(candidates),
          chosenCandidateIndex: !ownIdentification && index >= 0 && index < 10 ? index : null,
          commonName: saved.commonName,
          scientificName: saved.scientificName,
          category: saved.category,
          livestockId: created?.id ?? null,
        });
      }
      let photoAttachFailed = false;
      if (options?.attachPhoto && created?.id && photoFile) {
        photoAttachFailed = !(await attachPhoto(created.id, photoFile));
      }
      // The temporary identification upload is no longer needed either way.
      if (tempPhotoKey) {
        deleteTempPhoto({ aquariumId, key: tempPhotoKey });
      }
      resetState();
      onSaved({ photoAttachFailed });
      onClose();
      restoreFocus();
    },
    [
      aquariumId, attachPhoto, candidates, deleteTempPhoto, onClose, onSaved, ownIdentification, photoFile,
      resetState, restoreFocus, selectedCandidate, sendFeedback, tempPhotoKey,
    ],
  );

  let title = t('identify.capture.title');
  if (step === 'results') {
    title = t('identify.results.title');
  } else if (step === 'prefill') {
    title = t('identify.prefill.title');
  }

  return (
    <Dialog isOpen={isOpen} onClose={handleCancel} title={title} size="lg" data-testid="livestock-identify-flow">
      {step === 'capture' && (
        <IdentifyCaptureStep
          onSubmit={handleCaptureSubmit}
          onCancel={handleCancel}
          isLoading={isSubmitting}
          error={error}
          onRetry={handleRetryCapture}
        />
      )}
      {step === 'results' && (
        <IdentifyResultsStep
          candidates={candidates}
          selectedId={selectedId}
          onSelect={handleSelectCandidate}
          onAccept={handleAccept}
          onAcceptOwn={handleAcceptOwn}
          onNoMatch={handleNoMatch}
          onRetry={handleResultsRetry}
          onCancel={handleCancel}
          isLoading={false}
          error={null}
        />
      )}
      {step === 'prefill' && (prefillCandidate || ownIdentification) && (
        <IdentifyPrefillFormStep
          aquariumId={aquariumId}
          candidate={ownIdentification ? null : prefillCandidate}
          ownIdentification={ownIdentification}
          tempPhotoKey={tempPhotoKey}
          isOpen={isOpen}
          onSaved={handlePrefillSaved}
          onCancel={handleCancel}
        />
      )}
    </Dialog>
  );
}

export default LivestockIdentifyFlow;
